/**
 * Peças do shell para o contexto delegado (docs/PLANO_DELEGACAO.md — Bloco 4).
 *
 * Atuar na conta do titular é o uso NORMAL do secretário, o dia inteiro — não uma
 * exceção a alertar. Por isso o contexto vive no "chrome" do app, sempre visível e
 * sem tomar área de conteúdo (padrão de seletor de workspace/conta):
 *
 * - `SeletorDeConta`: no topo da barra lateral (e chip no cabeçalho do celular) —
 *   mostra de quem é a conta e troca entre a própria e as delegadas.
 * - `AtuacaoFilete`: filete âmbar de 3px no topo da tela enquanto se está na conta de
 *   outra pessoa — periférico, mas a tela "parece diferente" o tempo todo.
 * - `SincronizarAtuacao`: valida o contexto guardado na aba contra as delegações
 *   ativas (revogada/vencida → volta para a própria conta) e atende o link dos
 *   e-mails (`?atuando=<titular>`).
 */
import { isAxiosError } from 'axios';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { contaDaSupervisao, useDelegacoesRecebidas, useTrocarAtuacao } from '@/hooks/useDelegacoes';
import { useSupervisionados } from '@/hooks/useVisaoDoEscritorio';
import { registrarVisualizacao } from '@/api/compartilhamento';
import { RENOVAR_VISUALIZACAO_MS, setAtuacao, useAtuacao } from '@/lib/delegacao/atuacao';
import { descartarOfflineDaConta } from '@/lib/pwa/clearOfflineData';
import { cn } from '@/lib/utils';

const NIVEL_ROTULO = { LEITURA: 'Leitura', COLABORACAO: 'Colaboração' } as const;

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase() || '?';
}

/** Filete do topo + o anúncio para leitor de tela (a faixa visual é só cor). */
export function AtuacaoFilete() {
  const atuacao = useAtuacao();
  if (!atuacao) return null;
  return (
    <>
      <div aria-hidden="true" className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[3px] bg-[var(--color-warning)]" />
      <p role="status" className="sr-only">
        {atuacao.supervisao ? 'Visualizando como' : 'Atuando na conta de'} {atuacao.titularNome}
      </p>
    </>
  );
}

interface SeletorProps {
  /** `rail`: barra recolhida (só o avatar); `chip`: cabeçalho do celular; padrão: barra aberta. */
  variante?: 'barra' | 'rail' | 'chip';
  onNavClick?: () => void;
  /** Só o avatar, mesmo atuando por alguém (a linha do processo nos autos do celular). */
  apenasAvatar?: boolean;
}

/**
 * Some para quem não tem delegação recebida nem está atuando — para o advogado comum
 * o app fica exatamente como antes.
 */
export function SeletorDeConta({ variante = 'barra', onNavClick, apenasAvatar = false }: SeletorProps) {
  const { user } = useAuth();
  const atuacao = useAtuacao();
  const { data: recebidas } = useDelegacoesRecebidas();
  const { data: supervisionados } = useSupervisionados();
  const trocar = useTrocarAtuacao();
  if (!user || (!atuacao && (recebidas?.length ?? 0) === 0 && (supervisionados?.length ?? 0) === 0)) return null;
  // Quem tem delegação na mesma conta entra por ela (pode colaborar); o "visualizar como" fica para os outros.
  const delegados = new Set((recebidas ?? []).map((d) => d.titular.id));
  const paraVisualizar = (supervisionados ?? []).filter((s) => !delegados.has(s.usuario.id));

  const nomeAtual = atuacao ? atuacao.titularNome : user.nome;
  const avatar = (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[0.7rem] font-semibold',
        atuacao
          ? // Tokens do tema (o modo escuro do app é `data-theme`, não a classe `dark:` do Tailwind).
            'bg-[var(--color-warning-bg)] text-[var(--color-warning)] ring-1 ring-[var(--color-warning)]/40'
          : 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]',
      )}
    >
      {iniciais(nomeAtual)}
    </span>
  );

  // No topo do celular, na própria conta, só o avatar: o nome "Minha conta" ocupava ~40% da
  // barra. Atuando na conta de alguém o nome do titular fica, porque é o aviso de onde se está.
  const soAvatar = apenasAvatar || variante === 'rail' || (variante === 'chip' && !atuacao);

  const escolher = (fn: () => void) => {
    fn();
    onNavClick?.();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label={atuacao ? `Conta de ${atuacao.titularNome} — trocar de conta` : 'Minha conta — trocar de conta'}
          className={cn(
            'h-auto font-normal',
            variante === 'barra' && 'w-full justify-start gap-2.5 px-2 py-2',
            soAvatar && (variante === 'chip' ? 'h-11 w-11 p-0' : 'h-10 w-10 p-0'),
            variante === 'chip' && !soAvatar && 'max-w-[11rem] gap-1.5 px-1.5 py-1',
            atuacao &&
              variante !== 'rail' &&
              'bg-[var(--color-warning-bg)] ring-1 ring-[var(--color-warning)]/30 hover:bg-[var(--color-warning)]/20',
          )}
        >
          {avatar}
          {!soAvatar && (
            <>
              <span className="min-w-0 flex-1 text-left">
                {variante === 'barra' && (
                  <span className="block text-[11px] leading-tight text-[var(--color-text-secondary)]">
                    {atuacao?.supervisao ? 'Visualizando como' : atuacao ? 'Atuando na conta de' : 'Conta'}
                  </span>
                )}
                <span className="block truncate text-[0.8rem] font-semibold leading-tight text-[var(--color-text-primary)]">
                  {atuacao ? atuacao.titularNome : 'Minha conta'}
                </span>
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-secondary)]" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side={variante === 'rail' ? 'right' : 'bottom'} className="w-64">
        <DropdownMenuLabel className="text-xs font-normal text-[var(--color-text-secondary)]">Trabalhar na conta de</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => escolher(() => trocar(null))}>
          <Check className={cn('h-4 w-4', atuacao ? 'opacity-0' : 'opacity-100')} />
          <span className="truncate">Minha conta</span>
        </DropdownMenuItem>
        {(recebidas ?? []).map((d) => (
          <DropdownMenuItem key={d.id} onClick={() => escolher(() => trocar(d))}>
            <Check className={cn('h-4 w-4', atuacao?.delegacaoId === d.id ? 'opacity-100' : 'opacity-0')} />
            <span className="min-w-0 flex-1 break-words">{d.titular.nome}</span>
            <span className="text-xs text-[var(--color-text-secondary)]">{NIVEL_ROTULO[d.nivel]}</span>
          </DropdownMenuItem>
        ))}
        {paraVisualizar.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-[var(--color-text-secondary)]">
              Visualizar como (só leitura)
            </DropdownMenuLabel>
            {paraVisualizar.map((s) => {
              const conta = contaDaSupervisao(s);
              return (
                <DropdownMenuItem key={conta.id} onClick={() => escolher(() => trocar(conta))}>
                  <Check className={cn('h-4 w-4', atuacao?.delegacaoId === conta.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0 flex-1 break-words">{s.usuario.nome}</span>
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        {atuacao && (
          <>
            <DropdownMenuSeparator />
            <p className="px-2 py-1.5 text-xs text-[var(--color-text-secondary)]">
              {atuacao.supervisao
                ? 'Só leitura. A visualização fica registrada em seu nome.'
                : `${NIVEL_ROTULO[atuacao.nivel]}${atuacao.permiteIa ? ' · com Chat de IA' : ''}. O que você fizer fica registrado em seu nome.`}
            </p>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SincronizarAtuacao() {
  const atuacao = useAtuacao();
  const { data: recebidas, isSuccess } = useDelegacoesRecebidas();
  const supervisionados = useSupervisionados();
  const trocar = useTrocarAtuacao();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pedido = params.get('atuando');
  const tratado = useRef<string | null>(null);
  const encerrada = useRef<string | null>(null);

  // Delegação que deixou de valer enquanto a aba estava aberta (ou num F5): volta para
  // a própria conta e descarta o que ficou pendente no aparelho para aquela conta.
  useEffect(() => {
    if (!atuacao || encerrada.current === atuacao.delegacaoId) return;
    if (atuacao.supervisao) {
      // O "visualizar como" vale enquanto o membro estiver no escritório e o usuário for gestor.
      // Só com a lista carregada: ao entrar (e no F5) a de escritórios recomeça no contexto
      // novo, e a de supervisionados fica desligada até ela chegar.
      if (!supervisionados.isSuccess || supervisionados.data.some((s) => s.usuario.id === atuacao.titularId)) return;
      encerrada.current = atuacao.delegacaoId;
      toast.info(`Você não pode mais visualizar a conta de ${atuacao.titularNome}.`);
      trocar(null);
      return;
    }
    if (!isSuccess) return;
    if (!recebidas.some((d) => d.id === atuacao.delegacaoId)) {
      encerrada.current = atuacao.delegacaoId;
      toast.info(`Sua delegação na conta de ${atuacao.titularNome} não está mais ativa.`);
      void descartarOfflineDaConta(atuacao.titularId);
      trocar(null);
    }
  }, [isSuccess, recebidas, atuacao, trocar, supervisionados.isSuccess, supervisionados.data]);

  // A entrada registrada vale 12 h no servidor: numa carga depois de 6, registra de novo.
  useEffect(() => {
    if (!atuacao?.supervisao) return;
    if (atuacao.registradaEm && Date.now() - atuacao.registradaEm < RENOVAR_VISUALIZACAO_MS) return;
    registrarVisualizacao(atuacao.titularId)
      .then(() => setAtuacao({ ...atuacao, registradaEm: Date.now() }))
      .catch((err: unknown) => {
        // Só a recusa do servidor encerra; sem rede, o contexto fica (e o servidor decide depois).
        if (!isAxiosError(err) || err.response?.status !== 404) return;
        toast.info(`Você não pode mais visualizar a conta de ${atuacao.titularNome}.`);
        trocar(null);
      });
  }, [atuacao, trocar]);

  // Nível ou Chat de IA alterados pelo titular com esta aba aberta: atualiza o contexto
  // (sem trocar de conta — o cache continua o mesmo).
  useEffect(() => {
    if (!isSuccess || !atuacao || atuacao.supervisao) return;
    const atual = recebidas.find((d) => d.id === atuacao.delegacaoId);
    if (!atual || (atual.nivel === atuacao.nivel && atual.permite_ia === atuacao.permiteIa)) return;
    setAtuacao({ ...atuacao, nivel: atual.nivel, permiteIa: atual.permite_ia });
    toast.info(
      `Seu acesso à conta de ${atuacao.titularNome} mudou para ${NIVEL_ROTULO[atual.nivel]}${
        atual.permite_ia ? ', com Chat de IA' : ''
      }.`,
    );
  }, [isSuccess, recebidas, atuacao]);

  // Link do e-mail de intimações: `?atuando=<titular>`.
  useEffect(() => {
    if (!pedido || !isSuccess || tratado.current === pedido) return;
    tratado.current = pedido;
    const semParam = new URLSearchParams(params);
    semParam.delete('atuando');
    const destino = `${location.pathname}${semParam.toString() ? `?${semParam}` : ''}`;
    const delegacao = recebidas.find((d) => d.titular.id === pedido);
    if (!delegacao) {
      toast.info('Você não tem delegação ativa nessa conta.');
      setParams(semParam, { replace: true });
      return;
    }
    if (atuacao?.delegacaoId === delegacao.id) {
      navigate(destino, { replace: true });
      return;
    }
    trocar(delegacao, destino);
  }, [pedido, isSuccess, recebidas, atuacao, trocar, params, setParams, location.pathname, navigate]);

  return null;
}
